// Unified ProjectGeoOverview component
// Combines previous Project (project overview), RegionOverview and CouncilOverview pages
// Implements hierarchical breadcrumbs, scoped data fetching & caching, map highlighting, and drill-down navigation.

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Spin, Button } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { Polygon, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { useHistory } from "react-router-dom";
import API from "../../../API"; // projects API
import BaseMap from "../../Map/components/BaseMap";
import OverlayControl from "../../Map/components/OverlayControl";
import { DynamicProjectSummary, EnhancedProjectAnalytics, MapLegend } from "../../Map/components/MinimizableControls";
import { AppContext } from "../../../context/AppContext";
import DynamicBreadcrumbs from "../../components/DynamicBreadcrumbs";
import './styles.css';
import { normalizeRegionsResponse, normalizeCouncilsResponse } from "../../../Util/geo";

// Simple in-memory cache living for the session
const geoCache = {
  projectRegions: {}, // projectId -> regions[]
  projectCouncils: {}, // projectId -> councils[] (all councils if endpoint exists)
  regionCouncils: {}, // projectId -> { regionId -> councils[] }
  regionsById: {}, // regionId -> region
  councilsById: {}, // councilId -> council
};

// Utility: safe push bounds points
const collectGeomLatLngs = (geom, collector) => {
  if (!geom || !geom.coordinates) return;
  const pushRing = (ring) => ring.forEach(([lng, lat]) => collector.push([lat, lng]));
  if (geom.type === "Polygon") geom.coordinates.forEach(pushRing);
  else if (geom.type === "MultiPolygon") geom.coordinates.forEach((poly) => poly.forEach(pushRing));
};

const FitBounds = ({ regions, councils, focusedRegion, focusedCouncil, scope }) => {
  const map = useMap();
  const last = useRef(null);
  useEffect(() => {
    const latlngs = [];
    if (scope === "project") {
      if (Array.isArray(regions)) regions.forEach((r) => collectGeomLatLngs(r?.geometry, latlngs));
    } else if (scope === "region") {
      collectGeomLatLngs(focusedRegion?.geometry, latlngs);
      if (Array.isArray(councils)) councils.forEach((c) => collectGeomLatLngs(c?.geometry, latlngs));
    } else if (scope === "council") {
      collectGeomLatLngs(focusedCouncil?.geometry, latlngs);
      // fallback to parent region outline for context
      collectGeomLatLngs(focusedRegion?.geometry, latlngs);
    }
    if (!latlngs.length) return;
    const b = L.latLngBounds(latlngs);
    const id = `${scope}-${focusedRegion?.id || ''}-${focusedCouncil?.id || ''}`;
    // Debounce with rAF & avoid refitting same scope
    if (last.current === id) return;
    last.current = id;
    requestAnimationFrame(() => {
      try { map.fitBounds(b, { padding: [20, 20] }); } catch { /* noop */ }
    });
  }, [regions, councils, focusedRegion, focusedCouncil, map, scope]);
  return null;
};

const antIcon = <LoadingOutlined style={{ fontSize: 54 }} spin />;

// Styling helpers
const regionStyle = (region, scope, focusedRegion) => {
  if (!region) return { color: "#1890ff", weight: 2, fillOpacity: 0.2, opacity: 1 };
  if (scope === "project") {
    return { color: "#1890ff", weight: 1.5, fillOpacity: 0.25, opacity: 1 };
  }
  if (scope === "region") {
    const focused = focusedRegion?.id === region.id;
    return focused
      ? { color: "#1890ff", weight: 3, fillOpacity: 0.4, opacity: 1 }
      : { color: "#1890ff", weight: 1, fillOpacity: 0.05, opacity: 0.6, dashArray: "4 3" };
  }
  if (scope === "council") {
    // Show only outline for region
    const focused = focusedRegion?.id === region.id;
    return focused
      ? { color: "#1890ff", weight: 2.5, fillOpacity: 0, opacity: 1 }
      : { color: "#1890ff", weight: 1, fillOpacity: 0, opacity: 0.4, dashArray: "4 3" };
  }
  return { color: "#1890ff", weight: 2, fillOpacity: 0.2, opacity: 1 };
};

const councilStyle = (council, scope, focusedCouncil) => {
  if (scope === "project") {
    return { color: "#52c41a", weight: 1, fillOpacity: 0.15, opacity: 0.9 };
  }
  if (scope === "region") {
    return { color: "#52c41a", weight: 1.5, fillOpacity: 0.35, opacity: 1 };
  }
  if (scope === "council") {
    const focused = focusedCouncil?.id === council.id;
    return focused
      ? { color: "#13c2c2", weight: 3, fillOpacity: 0.45, opacity: 1 }
      : { color: "#13c2c2", weight: 1, fillOpacity: 0.04, opacity: 0.5, dashArray: "3 2" };
  }
  return { color: "#52c41a", weight: 1.2, fillOpacity: 0.2, opacity: 1 };
};

const ProjectGeoOverview = (props) => {
  const params = props?.match?.params || {};
  const projectId = params.projectId;
  const regionIdParam = params.regionId;
  const councilIdParam = params.councilId;
  const scope = councilIdParam ? "council" : regionIdParam ? "region" : "project";
  const history = useHistory();
  const { projects } = useContext(AppContext);
  const setSelectedCouncilId = (id) =>  null;
  const [selectedRegionId, setSelectedRegionId] = useState(null);

  const projectObj = useMemo(() => Array.isArray(projects) ? projects.find(p => `${p.id}` === `${projectId}`) : null, [projects, projectId]);
  const [projectInfo, setProjectInfo] = useState(null);
  // Redirect away immediately if route param is invalid to avoid calling /projects/null
  useEffect(() => {
    const invalid = projectId === undefined || projectId === null || projectId === '' || `${projectId}`.toLowerCase() === 'null' || `${projectId}`.toLowerCase() === 'undefined';
    if (invalid) {
  history.replace('/');
    }
  }, [projectId, history]);

  // Lazy fetch project details (predictable shape: { success, data: { code, ... } })
  useEffect(() => {
    let mounted = true;
    const invalid = projectId === undefined || projectId === null || projectId === '' || `${projectId}`.toLowerCase() === 'null' || `${projectId}`.toLowerCase() === 'undefined';
    if (!projectObj && !invalid && projectId && typeof API.getProject === 'function') {
      API.getProject(projectId)
        .then(res => { if (!mounted) return; setProjectInfo(res); })
        .catch(() => {});
    }
    return () => { mounted = false; };
  }, [projectObj, projectId]);
  const projectCode = useMemo(() => {
    if (projectObj?.code) return projectObj.code;
    if (projectInfo?.data?.code) return projectInfo.data.code;
    if (projectInfo?.code) return projectInfo.code;
    return `PRJ-${projectId}`; // fallback
  }, [projectObj, projectInfo, projectId]);

  // Local state
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]); // all project regions (cached)
  const [allCouncils, setAllCouncils] = useState([]); // optional project-level councils
  const [regionCouncils, setRegionCouncils] = useState([]); // councils for current region
  const [focusedRegion, setFocusedRegion] = useState(null);
  const [focusedCouncil, setFocusedCouncil] = useState(null);
  const [errorRegionNotFound, setErrorRegionNotFound] = useState(false);
  const [errorCouncilNotFound, setErrorCouncilNotFound] = useState(false);
  const [showRegionsLayer, setShowRegionsLayer] = useState(true);
  const [showCouncilsLayer, setShowCouncilsLayer] = useState(true);
  const [regionsOpacity, setRegionsOpacity] = useState(0.25);
  const [councilsOpacity, setCouncilsOpacity] = useState(0.2);

  // Fetch functions with caching
  const fetchProjectRegions = useCallback(async () => {
    if (geoCache.projectRegions[projectId]) return geoCache.projectRegions[projectId];
    try {
      const res = await API.getProjectRegions(projectId);
      const normalized = normalizeRegionsResponse(res.data);
      geoCache.projectRegions[projectId] = normalized;
      normalized.forEach(r => { geoCache.regionsById[r.id] = r; });
      return normalized;
    } catch {
      return [];
    }
  }, [projectId]);

  const fetchProjectCouncils = useCallback(async () => {
    if (geoCache.projectCouncils[projectId]) return geoCache.projectCouncils[projectId];
    if (!API.getProjectCouncils) return [];
    try {
      
      const res = await API.getProjectCouncils(projectId);
      const normalized = normalizeCouncilsResponse(res.data);
      geoCache.projectCouncils[projectId] = normalized;
      normalized.forEach(c => { geoCache.councilsById[c.id] = c; });
 
      return normalized;
    } catch { return []; }
  }, [projectId]);

  const fetchRegionAndCouncils = useCallback(async (rId) => {
    // Try caches first
    let regionObj = geoCache.regionsById[rId];
    if (!regionObj) {
      try {
        const res = await API.getProjectRegion(projectId, rId);
        const norm = normalizeRegionsResponse(res.data);
        regionObj = norm?.[0];
        if (regionObj) {
          geoCache.regionsById[rId] = regionObj;
          if (!geoCache.projectRegions[projectId]) geoCache.projectRegions[projectId] = [regionObj];
        }
      } catch {/* swallow */}
    }
    let councilsArr = geoCache.regionCouncils?.[projectId]?.[rId];
    if (!councilsArr) {
      try {
        const res = await API.getProjectRegionCouncils(projectId, rId);
        const norm = normalizeCouncilsResponse(res.data);
        councilsArr = norm;
        geoCache.regionCouncils[projectId] = geoCache.regionCouncils[projectId] || {};
        geoCache.regionCouncils[projectId][rId] = councilsArr;
        councilsArr.forEach(c => { geoCache.councilsById[c.id] = c; });
      } catch {/* swallow */}
    }
    return { regionObj, councilsArr: councilsArr || [] };
  }, [projectId]);

  const fetchCouncil = useCallback(async (cId) => {
    let councilObj = geoCache.councilsById[cId];
    if (!councilObj) {
      try {
        const res = await API.getProjectCouncil(projectId, cId);
        const norm = normalizeCouncilsResponse(res.data);
        councilObj = norm?.[0];
        if (councilObj) geoCache.councilsById[cId] = councilObj;
      } catch {/* swallow */}
    }
    return councilObj || null;
  }, [projectId]);

  // Scope state machine
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setErrorRegionNotFound(false);
    setErrorCouncilNotFound(false);

    const load = async () => {
      const invalid = projectId === undefined || projectId === null || projectId === '' || `${projectId}`.toLowerCase() === 'null' || `${projectId}`.toLowerCase() === 'undefined';
      if (invalid) { setLoading(false); return; }
      if (scope === "project") {
        const [regs] = await Promise.all([fetchProjectRegions()]);
        if (!mounted) return; setRegions(regs); setFocusedRegion(null); setFocusedCouncil(null);
        // Councils lazy unless previously loaded (cache) or layer toggled on later
        setRegionCouncils([]);
        setSelectedRegionId(null); setSelectedCouncilId(null);
      } else if (scope === "region") {
        const regs = await fetchProjectRegions();
        const { regionObj, councilsArr } = await fetchRegionAndCouncils(regionIdParam);
        if (!mounted) return;
        setRegions(regs);
        setFocusedRegion(regionObj);
        setRegionCouncils(councilsArr);
        if (!regionObj) setErrorRegionNotFound(true);
        setSelectedRegionId(regionObj?.id || regionIdParam);
        console.log('setSelectedRegionId inside ProjectGeoOverview.js', regionObj?.id || regionIdParam);
        setSelectedCouncilId(null);
      } else if (scope === "council") {
        const regs = await fetchProjectRegions();
        const councilObj = await fetchCouncil(councilIdParam);
        // Attempt to find its parent region: from selection, from caches intersection, or leave null
        let parentRegion = null;
        if (selectedRegionId) parentRegion = geoCache.regionsById[selectedRegionId];
        if (!parentRegion && councilObj) {
          // Try each region council list to find membership (cheap enough typical sizes)
            for (const r of regs) {
              const { councilsArr } = await fetchRegionAndCouncils(r.id);
              if (councilsArr.find(c => `${c.id}` === `${councilObj.id}`)) { parentRegion = r; break; }
            }
        }
        if (!mounted) return;
        setRegions(regs);
        setFocusedRegion(parentRegion);
        setFocusedCouncil(councilObj);
        if (!councilObj) setErrorCouncilNotFound(true);
        setRegionCouncils(parentRegion ? (geoCache.regionCouncils?.[projectId]?.[parentRegion.id] || []) : []);
        if (parentRegion) {
          setSelectedRegionId(parentRegion.id);
          console.log('setSelectedRegionId inside ProjectGeoOverview.js', parentRegion.id);
        }
        setSelectedCouncilId(councilObj?.id || councilIdParam);
      }
      if (mounted) setLoading(false);
    };
    load();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, regionIdParam, councilIdParam, projectId]);

  // Lazy load project councils if user toggles councils layer at project scope
  useEffect(() => {
    if (scope === "project" && showCouncilsLayer) {
      fetchProjectCouncils().then(cs => setAllCouncils(cs));
    }
  }, [scope, showCouncilsLayer, fetchProjectCouncils]);

  // Breadcrumbs
  const breadcrumbs = useMemo(() => {
    // First breadcrumb is the project itself (no leading 'Projects' list)
  const projectCrumb = { title: `Project(${projectCode})`, url: `/project/${projectId}/overview`, name: `Project ${projectCode}`, current: scope === 'project' };
    const arr = [projectCrumb];

    if (scope !== 'project') {
  const regionTitle = errorRegionNotFound ? 'Region(Not found)' : focusedRegion ? `Region(${focusedRegion?.name})` : 'Region(Loading…)';
      arr.push({
        title: regionTitle,
  url: `/project/${projectId}/regions/${focusedRegion?.id || regionIdParam}/overview`,
        name: regionTitle,
        loading: !focusedRegion && !errorRegionNotFound,
        current: scope === 'region',
        action: errorRegionNotFound ? (
          <Button size="small" onClick={() => history.push(`/project/${projectId}/overview`)}>Back to Project</Button>
        ) : undefined
      });
    }
    if (scope === 'council') {
  const councilTitle = errorCouncilNotFound ? 'LGA(Not found)' : focusedCouncil ? `LGA(${focusedCouncil?.name})` : 'LGA(Loading…)';
      arr.push({
        title: councilTitle,
  url: `/project/${projectId}/councils/${focusedCouncil?.id || councilIdParam}/lga-overview`,
        name: councilTitle,
        loading: !focusedCouncil && !errorCouncilNotFound,
        current: true,
        action: errorCouncilNotFound ? (
          <Button size="small" onClick={() => history.push(focusedRegion ? `/project/${projectId}/regions/${focusedRegion.id}/overview` : `/project/${projectId}/overview`)}>Back</Button>
        ) : undefined
      });
    }

    // Ensure only the last crumb is marked current
    const lastIndex = arr.length - 1;
    arr.forEach((c, i) => c.current = i === lastIndex);
    return arr;
  }, [projectId, projectCode, scope, focusedRegion, focusedCouncil, errorRegionNotFound, errorCouncilNotFound, regionIdParam, councilIdParam, history]);

  // Polygon click handlers
  const onRegionClick = (region) => {
    const rid = region?.id;
    if (!rid) return;
    setSelectedRegionId(rid);
    console.log('setSelectedRegionId inside ProjectGeoOverview.js', rid);
    history.push(`/project/${projectId}/regions/${rid}/overview`);
  };
  const onCouncilClick = (council) => {
    const cid = council?.id;
    if (!cid) return;
    setSelectedCouncilId(cid);
    history.push(`/project/${projectId}/councils/${cid}/lga-overview`);
  };

  // Determine which regions & councils to display based on scope
  // When viewing a single council, suppress region polygons for a cleaner LGA-focused view
  const displayedRegions = scope === 'council' ? [] : regions;
  const displayedCouncils = scope === 'project' ? (showCouncilsLayer ? allCouncils : [])
    : scope === 'region' ? (showCouncilsLayer ? regionCouncils : [])
      : scope === 'council' ? (showCouncilsLayer ? [focusedCouncil, ...(regionCouncils.filter(c => c?.id !== focusedCouncil?.id))] : [])
        : [];

  return (
    <div className="MapDashboard geo-overview" data-testid="project-geo-overview">
      {!loading ? (
        <BaseMap
          position={[-5.856, 34.074]}
          localLayers={[
            { id: 'regions', label: 'Regions', color: '#1890ff', checked: showRegionsLayer, onChange: setShowRegionsLayer, opacity: regionsOpacity, onOpacityChange: setRegionsOpacity, details: { abstract: 'Administrative Regions', data_quality_statement: 'Source: /projects/:id/regions', supplemental_information: 'Click to drill down' } },
            { id: 'councils', label: 'Councils (LGAs)', color: '#52c41a', checked: showCouncilsLayer, onChange: setShowCouncilsLayer, opacity: councilsOpacity, onOpacityChange: setCouncilsOpacity, details: { abstract: 'Local Government Areas', data_quality_statement: 'Source: /projects/:id/councils', supplemental_information: 'Click to drill down' } },
          ]}
        >
         
          <OverlayControl position="topleft" className="map-breadcrumbs" stopPointerEvents>
            <div className="overlay-row">
              <DynamicBreadcrumbs breadcrumbs={breadcrumbs} />
            </div>
          </OverlayControl>
          <FitBounds scope={scope} regions={displayedRegions} councils={displayedCouncils} focusedRegion={focusedRegion} focusedCouncil={focusedCouncil} />

          {/* Bottom-center dock for minimized controls */}
          <div id="map-bottom-center-dock" className="map-bottom-center-dock" />

          {/* Minimizable Map Controls */}
          <EnhancedProjectAnalytics id="project-analytics" position="topright" defaultMinimized={true} dock="bottom-center" />
          <DynamicProjectSummary id="project-summary" position="topleft" defaultMinimized={false} dock="bottom-center" />
          <MapLegend id="map-legend" position="bottomright" defaultMinimized={false} dock="bottom-center" />

          {showRegionsLayer && displayedRegions.map(region => {
            const geom = region?.geometry; if (!geom) return null;
            const style = regionStyle(region, scope, focusedRegion);
            const toLatLngs = (coords) => coords.map(([lng, lat]) => [lat, lng]);
            const clickHandlers = scope === 'project' || scope === 'region' ? { click: () => onRegionClick(region) } : {};
            if (geom.type === 'Polygon') {
              const rings = geom.coordinates.map(rg => toLatLngs(rg));
              return (
                <Polygon key={`region-${region.id}`} positions={rings} pathOptions={{ ...style, fillOpacity: style.fillOpacity ?? regionsOpacity }} eventHandlers={clickHandlers}>
                  <Tooltip>{region?.name || 'Region'}</Tooltip>
                </Polygon>
              );
            }
            if (geom.type === 'MultiPolygon') {
              return geom.coordinates.map((poly, idx) => (
                <Polygon key={`region-${region.id}-${idx}`} positions={poly.map(rg => toLatLngs(rg))} pathOptions={{ ...style, fillOpacity: style.fillOpacity ?? regionsOpacity }} eventHandlers={clickHandlers}>
                  <Tooltip>{region?.name || 'Region'}</Tooltip>
                </Polygon>
              ));
            }
            return null;
          })}

          {showCouncilsLayer && displayedCouncils.filter(c => c && c.geometry).map(council => {
            const geom = council?.geometry; if (!geom) return null;
            const style = councilStyle(council, scope, focusedCouncil);
            const toLatLngs = (coords) => coords.map(([lng, lat]) => [lat, lng]);
            const clickHandlers = (scope === 'project' || scope === 'region') ? { click: () => onCouncilClick(council) } : {};
            if (geom.type === 'Polygon') {
              const rings = geom.coordinates.map(rg => toLatLngs(rg));
              return (
                <Polygon key={`council-${council.id}`} positions={rings} pathOptions={{ ...style, fillOpacity: style.fillOpacity ?? councilsOpacity }} eventHandlers={clickHandlers}>
                  <Tooltip>{council?.name || council?.properties?.Authority || 'Council'}</Tooltip>
                </Polygon>
              );
            }
            if (geom.type === 'MultiPolygon') {
              return geom.coordinates.map((poly, idx) => (
                <Polygon key={`council-${council.id}-${idx}`} positions={poly.map(rg => toLatLngs(rg))} pathOptions={{ ...style, fillOpacity: style.fillOpacity ?? councilsOpacity }} eventHandlers={clickHandlers}>
                  <Tooltip>{council?.name || council?.properties?.Authority || 'Council'}</Tooltip>
                </Polygon>
              ));
            }
            return null;
          })}
        </BaseMap>
      ) : (
        <Spin
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '70vh' }}
          indicator={antIcon}
        />
      )}
    </div>
  );
};

export default ProjectGeoOverview;
